import { useAuth } from '@app/icare/client/auth'
import PatientStatusBadge from '@app/icare/components/status/PatientStatusBadge'
import PatientTitle from '@app/icare/entities/patient/components/PatientTitle'
import { PatientDeceasedEditDrawer } from '@app/icare/entities/patient/edits/PatientDeceasedEditDrawer'
import { PatientStatusEditDrawer } from '@app/icare/entities/patient/edits/PatientStatusEditDrawer'
import { patientQueryOptions } from '@app/icare/entities/patient/queries/patientQueryOptions'
import type { PatientIdType } from '@app/icare/entities/patient/schema/Patient'
import { useRouteTour } from '@app/icare/module/tour/useRouteTour'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useExtra, usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Flex, Masonry, Tour } from 'antd'
import { useCallback, useMemo, useRef, useState } from 'react'
import { PatientAddressAlerts } from '../-components/PatientAddressAlerts'
import { CareManagerCard } from './-cards/CareManagerCard'
import { CostCarrierCard } from './-cards/CostCarrierCard'
import { FindingsCard } from './-cards/FindingsCard'
import { GeneralPatientDataCard } from './-cards/GeneralPatientDataCard'
import { NotificationsCard } from './-cards/NotificationsCard'
import { PrescriberCard } from './-cards/PrescriberCard'
import { TherapiesCard } from './-cards/TherapiesCard'
import { WeightHistoryCard } from './-cards/WeightHistoryCard'
import { CardErrorBoundary } from './-components/CardErrorBoundary'

export const Route = createFileRoute('/_app/patienten/$id/_overview/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { id: patientId }: { id: PatientIdType } = Route.useParams()
  const [masonryLayoutNonce, setMasonryLayoutNonce] = useState(0)
  const lastFindingsLayoutSignalRef = useRef<string>('')
  const masonryRef = useRef<HTMLDivElement>(null)

  const tour = useRouteTour('patient-overview', [
    {
      title: t`Übersicht`,
      description: t`Hier siehst du die wichtigsten Informationen zur Patientenakte auf einen Blick — von Patientendaten bis zum Fortschritt der Akte.`,
      target: () => masonryRef.current,
    },
  ])

  const handleFindingsLayoutSignal = useCallback((signal: string) => {
    if (lastFindingsLayoutSignalRef.current === signal) {
      return
    }

    lastFindingsLayoutSignalRef.current = signal
    setMasonryLayoutNonce(current => current + 1)
  }, [])

  const { data: patient } = useSuspenseQuery(patientQueryOptions(patientId))
  const { allowed } = useAuth()
  const canEditStatus = patient ? allowed('update', patient, 'status') : false
  const pageHeaderTitle = useMemo(
    () =>
      (patient ? (
        <Flex gap="small" align="center">
          <PatientTitle value={patient} />
          <PatientStatusBadge
            value={patient.status}
            onClick={canEditStatus ? () => void PatientStatusEditDrawer.show({ patientId: patient.id }) : undefined}
          />
        </Flex>
      ) : (
        ''
      )) || t`Patient:in`,
    [patient, canEditStatus],
  )

  usePageContainerOverride(
    {
      header: {
        title: pageHeaderTitle,
      },
    },
    [pageHeaderTitle],
  )

  const canSeeNotifications = allowed('read', 'patientNotification')

  const items = useMemo(() => {
    return [
      {
        key: 'patient-data',
        data: { layoutNonce: masonryLayoutNonce },
        children: (
          <CardErrorBoundary cardName={t`Patientendaten`}>
            <GeneralPatientDataCard patient={patient} />
          </CardErrorBoundary>
        ),
      },
      {
        key: 'therapies',
        data: { layoutNonce: masonryLayoutNonce },
        children: (
          <CardErrorBoundary cardName={t`Therapien`}>
            <TherapiesCard patient={patient} />
          </CardErrorBoundary>
        ),
      },
      {
        key: 'cost-carrier',
        data: { layoutNonce: masonryLayoutNonce },
        children: (
          <CardErrorBoundary cardName={t`Kostenträger`}>
            <CostCarrierCard patient={patient} />
          </CardErrorBoundary>
        ),
      },
      {
        key: 'findings',
        data: { layoutNonce: masonryLayoutNonce },
        children: (
          <CardErrorBoundary cardName={t`Fortschritt der Akte`}>
            <FindingsCard patientId={patientId} onLayoutSignalChange={handleFindingsLayoutSignal} />
          </CardErrorBoundary>
        ),
      },
      {
        key: 'prescriber',
        data: { layoutNonce: masonryLayoutNonce },
        children: (
          <CardErrorBoundary cardName={t`Aktive Rezeptierer`}>
            <PrescriberCard patient={patient} />
          </CardErrorBoundary>
        ),
      },
      {
        key: 'weight-history',
        data: { layoutNonce: masonryLayoutNonce },
        children: (
          <CardErrorBoundary cardName={t`Gewichtsverlauf`}>
            <WeightHistoryCard patientId={patientId} />
          </CardErrorBoundary>
        ),
      },
      {
        key: 'care-manager',
        data: { layoutNonce: masonryLayoutNonce },
        children: (
          <CardErrorBoundary cardName={t`Versorgungsmanager`}>
            <CareManagerCard patient={patient} />
          </CardErrorBoundary>
        ),
      },
      ...(canSeeNotifications
        ? [
            {
              key: 'notifications',
              data: { layoutNonce: masonryLayoutNonce },
              children: (
                <CardErrorBoundary cardName={t`Meldungen`}>
                  <NotificationsCard patientId={patientId} />
                </CardErrorBoundary>
              ),
            },
          ]
        : []),
    ]
  }, [handleFindingsLayoutSignal, canSeeNotifications, masonryLayoutNonce, patient, patientId])

  useExtra(
    !patient?.deceased?.deceased && (
      <Button onClick={() => void PatientDeceasedEditDrawer.show({ patientId })} size="small" shape="round">
        <Trans>Sterbefall erfassen</Trans>
      </Button>
    ),
    [patient?.deceased?.deceased, patientId],
  )

  return (
    <>
      <PatientAddressAlerts patientId={patientId} />
      <div ref={masonryRef}>
        <Masonry columns={{ xs: 1, sm: 2, md: 2, lg: 2, xxl: 3 }} items={items} gutter={16} />
      </div>
      <Tour {...tour} />
    </>
  )
}
